'use client';

import { useState, useEffect, memo, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { WatchlistItem, useStore } from '@/lib/store';
import { tmdb } from '@/lib/tmdb';
import { parseTMDBDate, formatTMDBDate } from '@/lib/utils';
import { isEpisodeAvailable, seasonHasUnairedEpisodes, firstUnairedEpisode, seasonHasDisplayableData } from '@/lib/episodeAvailability';
import { AddToPlaylistButton } from '@/components/AddToPlaylistButton';
import { BuddyService } from '@/lib/buddies';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tv, Film, Sparkles, Check, Calendar, CheckCircle2, Circle, X, PlayCircle, Clock, AlertCircle, ChevronDown, Star, Repeat, RotateCcw, Lock } from 'lucide-react';
import { format } from 'date-fns';
import { ratingsService } from '@/lib/ratings';
import { useAuth } from '@/contexts/AuthContext';

interface WatchlistCardProps {
  item: WatchlistItem;
  showQuickComplete?: boolean;
  priority?: boolean;
  highlighted?: boolean;
  onOpenDialog?: (itemId: number, itemType: 'tv' | 'movie') => void;
}

interface SeasonDetail {
  season_number: number;
  episode_count: number;
  poster_path: string | null;
  name: string;
  air_date: string | null;
  episodes?: Array<{
    air_date: string | null;
    episode_number: number;
    name: string;
  }>;
}

const WatchlistCardComponent = ({ item, showQuickComplete = false, priority = false, highlighted = false, onOpenDialog }: WatchlistCardProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const { markSeasonWatched, markSeasonUnwatched, markEpisodeWatched, markEpisodeUnwatched, toggleCurrentlyWatching, toggleMovieWatched, setMovieWatchState, removeFromWatchlist, updateWatchlistItem, setPersonalRank } = useStore();
  const [showSeasonMenu, setShowSeasonMenu] = useState(false);
  const [seasons, setSeasons] = useState<SeasonDetail[]>([]);
  const [loadingSeasons, setLoadingSeasons] = useState(false);
  const [showDesktopSeasonDialog, setShowDesktopSeasonDialog] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [expandedSeasons, setExpandedSeasons] = useState<Set<number>>(new Set());
  const [showRatingPicker, setShowRatingPicker] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationEpisode, setCelebrationEpisode] = useState<{ season: number; episode: number } | null>(null);
  const seasonScrollRef = useRef<HTMLDivElement>(null);

  // Get the next episode to watch
  const getNextEpisodeToWatch = () => {
    if (item.type !== 'tv' || !item.totalSeasons) return null;

    // Find first unwatched season
    for (let seasonNum = 1; seasonNum <= item.totalSeasons; seasonNum++) {
      if (!item.watchedSeasons.includes(seasonNum)) {
        // Found an unwatched season - find first unwatched episode
        const watchedEpisodes = item.watchedEpisodes?.[seasonNum] || [];
        const season = seasons.find(s => s.season_number === seasonNum);
        const totalEpisodes = season?.episodes?.length || season?.episode_count || 0;

        // Find first unwatched episode number
        for (let epNum = 1; epNum <= totalEpisodes; epNum++) {
          if (!watchedEpisodes.includes(epNum)) {
            return {
              season: seasonNum,
              episode: epNum,
              totalInSeason: totalEpisodes,
              seasonData: season
            };
          }
        }
      }
    }

    return null;
  };

  // Get the next season to watch (legacy, for other parts of the code)
  const getNextSeasonToWatch = () => {
    if (item.type !== 'tv' || !item.totalSeasons) return null;
    const watchedCount = item.watchedSeasons.length;
    if (watchedCount >= item.totalSeasons) return null;

    // Find the first unwatched season
    for (let i = 1; i <= item.totalSeasons; i++) {
      if (!item.watchedSeasons.includes(i)) {
        return i;
      }
    }
    return null;
  };

  // Check if the quick complete button should be locked (for episodes)
  const isQuickCompleteButtonLocked = (): { locked: boolean; reason?: string } => {
    const nextEpisode = getNextEpisodeToWatch();
    if (!nextEpisode) return { locked: false };

    // If seasons aren't loaded yet, assume unlocked (will validate on click)
    if (seasons.length === 0) return { locked: false };

    // Check if the episode has aired — undated episodes with nothing aired
    // after them count as unaired, not as fair game
    const season = nextEpisode.seasonData;
    if (!season?.episodes) return { locked: false };

    const episode = season.episodes.find((ep: any) => ep.episode_number === nextEpisode.episode);
    if (!episode) return { locked: false };

    if (!isEpisodeAvailable(episode, season.episodes)) {
      const airDate = parseTMDBDate(episode.air_date);
      return {
        locked: true,
        reason: airDate ? `Airs ${format(airDate, 'MMM d, yyyy')}` : 'Not aired yet',
      };
    }

    return { locked: false };
  };

  const handleQuickComplete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Load seasons if not already loaded. Keep a local reference — the
    // freshly fetched data must be used for validation below, because the
    // `seasons` state won't have updated within this same click
    let loadedSeasons = seasons;
    if (loadedSeasons.length === 0) {
      setLoadingSeasons(true);
      try {
        const show = await tmdb.getShowWithDetails(item.id);
        const sortedSeasons = (show.seasons || [])
          .filter((s: any) => s.season_number > 0)
          .sort((a: any, b: any) => a.season_number - b.season_number);

        // Fetch episode details for each season
        const seasonsWithEpisodes = await Promise.all(
          sortedSeasons.map(async (season: any) => {
            try {
              const seasonDetail = await tmdb.getSeason(show.id, season.season_number);
              return { ...season, episodes: seasonDetail.episodes || [] };
            } catch (error) {
              return season;
            }
          })
        );

        loadedSeasons = seasonsWithEpisodes;
        setSeasons(seasonsWithEpisodes);
        healSeasonCount(seasonsWithEpisodes);
      } catch (error) {
        setLoadingSeasons(false);
        return;
      }
      setLoadingSeasons(false);
    }

    // Find the next unwatched episode from the loaded season data
    let nextEpisode: { season: number; episode: number; totalInSeason: number; episodes: any[] } | null = null;
    for (let seasonNum = 1; seasonNum <= (item.totalSeasons || 0); seasonNum++) {
      if (item.watchedSeasons.includes(seasonNum)) continue;
      const season = loadedSeasons.find(s => s.season_number === seasonNum);
      const episodes = season?.episodes || [];
      const totalEpisodes = episodes.length || season?.episode_count || 0;
      const watchedEpisodes = item.watchedEpisodes?.[seasonNum] || [];
      for (let epNum = 1; epNum <= totalEpisodes; epNum++) {
        if (!watchedEpisodes.includes(epNum)) {
          nextEpisode = { season: seasonNum, episode: epNum, totalInSeason: totalEpisodes, episodes };
          break;
        }
      }
      if (nextEpisode) break;
    }
    if (!nextEpisode) return;

    // Never mark an episode that hasn't aired (future date, or no date with
    // nothing aired after it)
    const episodeData = nextEpisode.episodes.find((ep: any) => ep.episode_number === nextEpisode!.episode);
    if (episodeData && !isEpisodeAvailable(episodeData, nextEpisode.episodes)) {
      const airDate = parseTMDBDate(episodeData.air_date);
      alert(`Cannot mark episode as watched yet. ${airDate ? `Airs ${format(airDate, 'MMM d, yyyy')}` : "It hasn't aired yet."}`);
      return;
    }

    // Show celebration animation
    setCelebrationEpisode({ season: nextEpisode.season, episode: nextEpisode.episode });
    setShowCelebration(true);

    // Mark the episode as watched
    await markEpisodeWatched(item.id, nextEpisode.season, nextEpisode.episode);

    // Check if season is now complete
    const watchedCount = (item.watchedEpisodes?.[nextEpisode.season]?.length || 0) + 1;
    if (watchedCount >= nextEpisode.totalInSeason) {
      // Auto-complete the season
      await markSeasonWatched(item.id, nextEpisode.season);
    }

    // Hide celebration after 3 seconds
    setTimeout(() => {
      setShowCelebration(false);
      setCelebrationEpisode(null);
    }, 3000);
  };

  // Helper function to validate season (extracted for reuse)
  const validateSeason = (season: any, seasonNumber: number): { canMark: boolean; reason?: string } => {
    // If we don't have season data at all, allow it (better to allow than block)
    if (!season) {
      return { canMark: true };
    }

    // Get status text for validation
    const statusText = getStatusText();

    // If no episode data, allow it (episode data might have failed to load)
    if (!season.episodes || season.episodes.length === 0) {
      return { canMark: true };
    }

    // Check for suspiciously few episodes
    if ((statusText === 'Active' || statusText === 'Returning') && season.episodes.length <= 3) {
      const hasFutureEpisodes = season.episodes.some((ep: any) => {
        if (!ep.air_date) return false;
        const epDate = parseTMDBDate(ep.air_date)!;
        const now = new Date();
        epDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        return epDate > now;
      });

      if (!hasFutureEpisodes) {
        return {
          canMark: false,
          reason: `Season appears incomplete (only ${season.episodes.length} episode${season.episodes.length !== 1 ? 's' : ''} found)`
        };
      }
    }

    // Block if the season still has unaired episodes (future-dated, or
    // undated with nothing aired after them)
    const unaired = firstUnairedEpisode(season.episodes);
    if (unaired) {
      const airDate = parseTMDBDate(unaired.air_date);
      return {
        canMark: false,
        reason: airDate
          ? `Episode ${unaired.episode_number} airs on ${format(airDate, 'MMM d, yyyy')}`
          : `Episode ${unaired.episode_number} hasn't aired yet`,
      };
    }

    return { canMark: true };
  };

  // Fully-watched shows get a restart button: wipes all progress (after
  // confirming) and drops the show back into Watching Now
  const handleRestartShow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm(`Restart ${item.name} from the beginning? All watched progress will be cleared.`)) return;
    await updateWatchlistItem(item.id, {
      watchedSeasons: [],
      watchedEpisodes: {},
      episodeWatchDates: {},
      currentlyWatching: true,
    });
    if (item.sharedProgress) BuddyService.syncProgress(item.id);
  };

  const handleToggleWatching = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleCurrentlyWatching(item.id);
  };

  const handleCycleMovieState = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (item.watched) {
      setMovieWatchState(item.id, 'idle');
    } else if (item.currentlyWatching) {
      setMovieWatchState(item.id, 'watched');
    } else {
      setMovieWatchState(item.id, 'watching');
    }
  };

  const watchedPercentage = item.type === 'tv' && item.totalSeasons
    ? (item.watchedSeasons.length / item.totalSeasons) * 100
    : 0;

  // Load season details when menu is opened
  useEffect(() => {
    if (showSeasonMenu && item.type === 'tv' && seasons.length === 0) {
      loadSeasonDetails();
    }
  }, [showSeasonMenu]);

  // Prevent body scroll when season menu is open
  useEffect(() => {
    if (showSeasonMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setDragOffset(0); // Reset drag offset when menu closes
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showSeasonMenu]);

  // Preload season data for quick complete button validation
  useEffect(() => {
    if (showQuickComplete && item.type === 'tv' && seasons.length === 0) {
      loadSeasonDetails();
    }
  }, [showQuickComplete]);

  // Load season details when desktop dialog is opened
  useEffect(() => {
    if (showDesktopSeasonDialog && item.type === 'tv' && seasons.length === 0) {
      loadSeasonDetails();
    }
  }, [showDesktopSeasonDialog]);

  // Migration logic: Convert season data to episode data for existing users
  const migrateSeasonDataToEpisodes = async () => {
    // Only migrate if we have watched seasons but no episode data
    if (item.type !== 'tv' || !item.watchedSeasons.length || item.watchedEpisodes) {
      return;
    }

    try {
      const show = await tmdb.getShowWithDetails(item.id);
      const watchedEpisodes: Record<number, number[]> = {};
      const episodeWatchDates: Record<string, string> = {};
      const migrationDate = new Date().toISOString();

      // For each watched season, fetch episodes and mark them all as watched
      for (const seasonNum of item.watchedSeasons) {
        const seasonData = await tmdb.getSeason(item.id, seasonNum);
        const episodeCount = seasonData.episodes?.length || 0;
        const episodeNumbers = Array.from({ length: episodeCount }, (_, i) => i + 1);

        watchedEpisodes[seasonNum] = episodeNumbers;

        // Record watch dates
        episodeNumbers.forEach(epNum => {
          episodeWatchDates[`S${seasonNum}E${epNum}`] = migrationDate;
        });
      }

      // Update the item with episode data
      updateWatchlistItem(item.id, {
        watchedEpisodes,
        episodeWatchDates,
      });
    } catch (error) {
      // Migration failed
    }
  };

  // Trigger migration when opening the desktop dialog
  useEffect(() => {
    if (showDesktopSeasonDialog && item.type === 'tv') {
      migrateSeasonDataToEpisodes();
    }
  }, [showDesktopSeasonDialog]);

  // The stored totalSeasons can lag behind (phantom 0/0 seasons are only
  // excluded once a content check runs) — correct it whenever real season
  // data is in hand so the card's "x/y Seasons" matches the tracker
  const healSeasonCount = (loadedSeasons: any[]) => {
    const displayable = loadedSeasons.filter(s => seasonHasDisplayableData(s)).length;
    if (displayable > 0 && item.totalSeasons !== displayable) {
      updateWatchlistItem(item.id, { totalSeasons: displayable });
    }
  };

  const loadSeasonDetails = async () => {
    setLoadingSeasons(true);
    try {
      const showDetails = await tmdb.getShowWithDetails(item.id);
      if (showDetails.seasons) {
        // Filter out season 0 (specials)
        const regularSeasons = showDetails.seasons.filter((s: any) => s.season_number > 0);

        // Fetch full season details with episodes for each season
        const seasonsWithEpisodes = await Promise.all(
          regularSeasons.map(async (season: any) => {
            try {
              const seasonDetail = await tmdb.getSeason(item.id, season.season_number);
              return {
                ...season,
                episodes: seasonDetail.episodes || []
              };
            } catch (error) {
              return season;
            }
          })
        );

        setSeasons(seasonsWithEpisodes);
        healSeasonCount(seasonsWithEpisodes);
      }
    } catch (error) {
      // Failed to load season details
    } finally {
      setLoadingSeasons(false);
    }
  };

  // Check if a season can be marked as watched (last episode must have aired)
  const canMarkSeasonWatched = (seasonNumber: number): { canMark: boolean; reason?: string } => {
    const season = seasons.find(s => s.season_number === seasonNumber);

    // If we don't have season data at all, allow it (better to allow than block)
    if (!season) {
      return { canMark: true };
    }

    // If no episode data, allow it (episode data might have failed to load)
    if (!season.episodes || season.episodes.length === 0) {
      return { canMark: true };
    }

    // Check for suspiciously few episodes - likely incomplete season data
    // If show is "Returning Series" and season has ≤3 episodes, it's probably incomplete
    const statusText = getStatusText();
    if ((statusText === 'Active' || statusText === 'Returning') && season.episodes.length <= 3) {
      // Check if any episodes have future air dates
      const hasFutureEpisodes = season.episodes.some(ep => {
        if (!ep.air_date) return false;
        const epDate = parseTMDBDate(ep.air_date)!;
        const now = new Date();
        epDate.setHours(0, 0, 0, 0);
        now.setHours(0, 0, 0, 0);
        return epDate > now;
      });

      // If no future episodes but very few episodes total, likely incomplete data
      if (!hasFutureEpisodes) {
        return {
          canMark: false,
          reason: `Season appears incomplete (only ${season.episodes.length} episode${season.episodes.length !== 1 ? 's' : ''} found)`
        };
      }
    }

    // Block if the season still has unaired episodes (future-dated, or
    // undated with nothing aired after them)
    const unaired = firstUnairedEpisode(season.episodes);
    if (unaired) {
      const airDate = parseTMDBDate(unaired.air_date);
      return {
        canMark: false,
        reason: airDate
          ? `Episode ${unaired.episode_number} airs on ${format(airDate, 'MMM d, yyyy')}`
          : `Episode ${unaired.episode_number} hasn't aired yet`,
      };
    }

    return { canMark: true };
  };

  const handleSeasonToggle = (seasonNumber: number) => {
    const isWatched = item.watchedSeasons.includes(seasonNumber);
    if (isWatched) {
      markSeasonUnwatched(item.id, seasonNumber);
    } else {
      // Check if season can be marked as watched
      const { canMark, reason } = canMarkSeasonWatched(seasonNumber);
      if (!canMark && reason) {
        // Show a toast or alert
        alert(`Cannot mark season as watched yet. ${reason}`);
        return;
      }
      markSeasonWatched(item.id, seasonNumber);
    }
  };

  // Drag handlers for mobile menu
  const handleDragStart = (e: React.TouchEvent) => {
    const scrollContainer = seasonScrollRef.current;
    if (scrollContainer && scrollContainer.scrollTop === 0) {
      setIsDragging(true);
      setDragStartY(e.touches[0].clientY);
    }
  };

  const handleDragMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const currentY = touch.clientY;
    const offset = Math.max(0, currentY - dragStartY);

    // Prevent default scroll behavior when pulling down
    if (offset > 0) {
      e.preventDefault();
    }

    setDragOffset(offset);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    // Close menu when dragged down ~18% of viewport height (proportional across phone sizes)
    if (dragOffset > Math.max(80, window.innerHeight * 0.18)) {
      setShowSeasonMenu(false);
    }
    // Reset offset
    setDragOffset(0);
    setDragStartY(0);
  };

  // Get badge color based on show status
  // Neutral glass pills with a subtle color hint — matches the cinematic palette
  // (no more chunky solid blue/green/yellow that fights the red+black identity).
  const getStatusBadgeColor = (statusText: string) => {
    switch (statusText) {
      case 'Active':
        return 'bg-black/70 text-green-300';
      case 'Returning':
        return 'bg-black/70 text-gray-200';
      case 'Ended':
        return 'bg-black/70 text-gray-400';
      case 'Cancelled':
        return 'bg-black/70 text-red-300';
      case 'In Production':
        return 'bg-black/70 text-amber-200';
      default:
        return 'bg-black/70 text-gray-300';
    }
  };

  // Check if show has episodes currently airing (in middle of a season)
  const isCurrentlyAiring = () => {
    if (!item.nextEpisode || !item.nextEpisode.airDate) return false;

    try {
      const nextEpisodeDate = parseTMDBDate(item.nextEpisode.airDate)!;
      const now = new Date();

      // Check if next episode is within the next 14 days (recently aired or about to air)
      const diffInDays = (nextEpisodeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);

      return diffInDays >= -7 && diffInDays <= 14; // Episode aired in last week or airs in next 2 weeks
    } catch {
      return false;
    }
  };

  // Generate new content badge text
  const getNewContentText = () => {
    const statusText = getStatusText();

    // For Active shows (currently airing) with episode info
    if (statusText === 'Active' && item.nextEpisode) {
      return 'New Episode';
    }

    // For Returning shows or shows with new season number, show Season # format
    if (item.newSeasonNumber) {
      return `Season ${item.newSeasonNumber}`;
    }

    // For Returning shows with episode info, show Season # format
    if (statusText === 'Returning' && item.nextEpisode) {
      return `Season ${item.nextEpisode.season}`;
    }

    // Episode info but status isn't clear
    if (item.nextEpisode) {
      return 'New Episode';
    }

    if (item.type === 'tv' && item.totalSeasons) {
      const nextUnwatchedSeason = item.totalSeasons;
      if (nextUnwatchedSeason > item.watchedSeasons.length) {
        return `Season ${nextUnwatchedSeason}`;
      }
    }
    return 'NEW';
  };

  const getNewContentSubtext = () => {
    if (item.nextEpisode?.airDate) {
      try {
        const airDate = parseTMDBDate(item.nextEpisode.airDate)!;
        const now = new Date();
        if (airDate > now) {
          return `Airs ${format(airDate, 'MMM d')}`;
        }
        // The stored nextEpisode can go stale between content checks — never
        // claim "Available Now" for an episode the user already watched
        const { season, episode } = item.nextEpisode;
        const alreadyWatched =
          item.watchedSeasons?.includes(season) ||
          item.watchedEpisodes?.[season]?.includes(episode);
        return alreadyWatched ? null : 'Available Now';
      } catch {
        return null;
      }
    }
    return null;
  };

  // Get show status display text with better logic
  const getStatusText = () => {
    if (!item.status) return null;

    // If show is "Returning Series", determine if it's actively airing or just returning
    if (item.status === 'Returning Series') {
      if (isCurrentlyAiring()) {
        return 'Active'; // Episodes currently airing
      } else {
        return 'Returning'; // Show will return but not currently airing
      }
    }

    const statusMap: Record<string, string> = {
      'Ended': 'Ended',
      'Cancelled': 'Cancelled',
      'In Production': 'In Production',
    };

    return statusMap[item.status] || item.status;
  };

  // Check if show is completed and can be rewatched
  const canRewatch = () => {
    if (item.type !== 'tv') return false;
    const statusText = getStatusText();
    const isCompleted = item.totalSeasons && item.watchedSeasons.length >= item.totalSeasons;
    const isEndedOrCancelled = statusText === 'Ended' || statusText === 'Cancelled';
    const isNotInWatchingNow = !item.currentlyWatching;
    return isCompleted && isEndedOrCancelled && isNotInWatchingNow;
  };

  // Check if rating should be in top-left (for any show not in "Watching Now" with a rating)
  const shouldRatingBeTopLeft = () => {
    if (!item.personalRank) return false;
    const isNotInWatchingNow = !item.currentlyWatching;
    return isNotInWatchingNow && item.type === 'tv';
  };

  const handleStarClick = async (starIndex: number) => {
    // Convert star index (0-4) to database value (2, 4, 6, 8, 10)
    const value = (starIndex + 1) * 2;

    // Sync both rating systems
    if (user) {
      // First update the ratings collection
      await ratingsService.setUserRating(
        user.uid,
        item.id,
        item.type,
        value,
        {
          mediaTitle: item.name,
          posterPath: item.posterPath || undefined
        }
      );
      // Then update the watchlist personalRank
      await setPersonalRank(item.id, value);
    }

    setHoverRating(null);
  };

  const handleStarHover = (starIndex: number) => {
    // Convert star index (0-4) to database value (2, 4, 6, 8, 10)
    const value = (starIndex + 1) * 2;
    setHoverRating(value);
  };

  return (
    <>
      <div
        id={`watchlist-item-${item.id}`}
        className={`cursor-pointer w-full relative group/card transition-all duration-500 ${
          highlighted ? 'ring-4 ring-red-500 ring-offset-4 ring-offset-black rounded-2xl scale-105' : ''
        }`}
      >
        {onOpenDialog ? (
          <div
            className="space-y-2 cursor-pointer"
            onClick={() => onOpenDialog(item.id, item.type)}
          >
            {/* Poster Image Container with improved gradients */}
            <div
              className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-black ring-1 ring-white/10 transition-all duration-300 group-hover/card:ring-red-500/50 group-hover/card:shadow-[0_0_30px_-5px_rgba(220,38,38,0.45)] group-hover/card:-translate-y-1"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setHoverRating(null);
              }}
            >
              {/* Poster Image */}
              {item.posterPath ? (
                <Image
                  src={tmdb.getImageUrl(item.posterPath, 'w500')}
                  alt={item.name}
                  fill
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  className="object-cover transition-transform duration-500 group-hover/card:scale-105"
                  priority={priority}
                  loading={priority ? undefined : "lazy"}
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  {item.type === 'tv' ? (
                    <Tv className="h-16 w-16 text-gray-600" />
                  ) : (
                    <Film className="h-16 w-16 text-gray-600" />
                  )}
                </div>
              )}

              {/* Enhanced gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/20" />

              {/* Hover Overlay - Subtle darkening (Desktop only) */}
              {isHovering && !('ontouchstart' in window) && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 animate-in fade-in duration-200 z-10 pointer-events-none" />
              )}

              {/* Action rail — watch state, rating, and playlist stacked in
                  ONE consistent column instead of scattered around the card */}
              {(() => {
                const isTouch = 'ontouchstart' in window;
                const railHover = isTouch || isHovering;
                const circleBtn = 'flex items-center justify-center w-10 h-10 rounded-full bg-black/60 backdrop-blur-md shadow-xl transition-all duration-300 hover:scale-110 active:scale-95 hover:bg-black/70';
                const fade = (visible: boolean) =>
                  `transition-all duration-200 ${visible ? 'opacity-100 animate-in fade-in slide-in-from-left-2' : 'opacity-0 pointer-events-none'}`;

                return (
                  <div className="absolute top-2 left-2 z-30 flex flex-col gap-2">
                    {/* Watch state — always visible. Completed shows get a
                        restart button that clears progress for a rewatch. */}
                    {item.type === 'tv' ? (
                      watchedPercentage === 100 ? (
                        <div className={fade(true)}>
                          <button onClick={handleRestartShow} className={`${circleBtn} ring-1 ring-green-500/50`} title="Restart from the beginning">
                            <RotateCcw className="h-5 w-5 text-green-400" />
                          </button>
                        </div>
                      ) : (
                        <div className={fade(true)}>
                          <button
                            onClick={handleToggleWatching}
                            className={`${circleBtn} ring-1 ${item.currentlyWatching ? 'ring-red-500/60' : 'ring-white/15'}`}
                            title={item.currentlyWatching ? 'Remove from Watching Now' : 'Add to Watching Now'}
                          >
                            <PlayCircle className={`h-5 w-5 ${item.currentlyWatching ? 'text-red-500' : 'text-gray-300'}`} />
                          </button>
                        </div>
                      )
                    ) : (
                      (() => {
                        const state: 'idle' | 'watching' | 'watched' = item.watched ? 'watched' : item.currentlyWatching ? 'watching' : 'idle';
                        const ringClass = state === 'watched' ? 'ring-green-500/60' : state === 'watching' ? 'ring-red-500/70' : 'ring-white/15';
                        const MovieIcon = state === 'watched' ? CheckCircle2 : state === 'watching' ? Check : PlayCircle;
                        const iconClass = state === 'watched' ? 'text-green-500' : state === 'watching' ? 'text-red-500' : 'text-gray-300';
                        const movieTitle = state === 'watched'
                          ? 'Watched — click to clear'
                          : state === 'watching'
                            ? 'Currently watching — click to mark complete'
                            : 'Add to Currently Watching';
                        return (
                          <div className={fade(true)}>
                            <button onClick={handleCycleMovieState} className={`${circleBtn} ring-1 ${ringClass}`} title={movieTitle}>
                              <MovieIcon className={`h-5 w-5 ${iconClass}`} />
                            </button>
                          </div>
                        );
                      })()
                    )}

                    {/* Rate */}
                    <div className={fade(railHover || !!item.personalRank)}>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowRatingPicker(true);
                        }}
                        className={`${circleBtn} relative ring-1 ${item.personalRank ? 'ring-red-500/60' : 'ring-white/15'}`}
                        title={item.personalRank ? `Your Rating: ${item.personalRank / 2}/5` : 'Rate this'}
                      >
                        {item.personalRank ? (
                          <span className="text-white font-bold text-sm">{item.personalRank / 2}</span>
                        ) : (
                          <Star className="h-5 w-5 text-gray-300" />
                        )}
                      </button>
                    </div>

                    {/* Add to playlist */}
                    <div className={fade(railHover)}>
                      <AddToPlaylistButton
                        media={{ id: item.id, type: item.type, title: item.name, posterPath: item.posterPath }}
                        triggerClassName={`${circleBtn} ring-1 ring-white/15 text-gray-300 hover:text-white`}
                        iconClassName="h-5 w-5"
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Badge - Top Right — one consolidated pill instead of a stack.
                  Status text lives in the bottom season bar now. */}
              <div className="absolute top-2 right-2 flex flex-col items-end gap-1.5 z-10">
                {item.hasNewContent && getStatusText() !== 'Ended' && getStatusText() !== 'Cancelled' ? (
                  <div className="backdrop-blur-md bg-red-600/85 px-2.5 py-1 rounded-full text-white text-[10px] font-bold ring-1 ring-red-400/40 flex items-center gap-1 shadow-lg">
                    <Sparkles className="h-3 w-3" />
                    <span>
                      {getNewContentText()}
                      {getNewContentSubtext() ? ` · ${getNewContentSubtext()}` : ''}
                    </span>
                  </div>
                ) : (
                  item.nextEpisode && item.status === 'Returning Series' && getNewContentSubtext() && (
                    <div className="backdrop-blur-md bg-black/60 px-2.5 py-1 rounded-full text-white text-[10px] font-medium ring-1 ring-white/15 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{getNewContentSubtext()}</span>
                    </div>
                  )
                )}
              </div>

              {/* Season Selector Button - Bottom (TV shows only) */}
              {item.type === 'tv' && item.totalSeasons && item.totalSeasons > 0 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // If onOpenDialog is available, use the new dialog
                    if (onOpenDialog) {
                      onOpenDialog(item.id, item.type);
                    } else {
                      // Fallback to old behavior
                      const isDesktop = window.innerWidth >= 768;
                      if (isDesktop) {
                        setShowDesktopSeasonDialog(true);
                      } else {
                        setShowSeasonMenu(true);
                      }
                    }
                  }}
                  className="absolute bottom-0 left-0 right-0 z-20 group/season-btn text-left"
                >
                  {/* Cinematic gradient footer — flows out of the poster */}
                  <div className="pt-12 pb-3 px-3 bg-gradient-to-t from-black via-black/85 to-transparent transition-all">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-bold text-white flex items-baseline gap-1.5 drop-shadow-lg">
                        {item.watchedSeasons.length}/{item.totalSeasons} Seasons
                        {getStatusText() && (
                          <span className="text-[10px] font-medium text-gray-400">· {getStatusText()}</span>
                        )}
                      </span>
                      {watchedPercentage === 100 ? (
                        <span className="flex items-center gap-1 text-[10px] font-bold text-green-400">
                          <Check className="h-3 w-3" />
                          {!('ontouchstart' in window) && 'COMPLETE'}
                        </span>
                      ) : (
                        <ChevronDown className="h-4 w-4 text-gray-400 group-hover/season-btn:text-red-400 group-hover/season-btn:translate-y-0.5 transition-all" />
                      )}
                    </div>
                    <div className="h-1 bg-white/15 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          watchedPercentage === 100 ? 'bg-gradient-to-r from-green-500 to-green-400' : 'bg-gradient-to-r from-red-600 to-red-400'
                        }`}
                        style={{ width: `${watchedPercentage}%` }}
                      />
                    </div>
                  </div>
                </button>
              )}

              {/* Celebration Overlay */}
              {showCelebration && celebrationEpisode && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-500">
                  {/* Confetti particles */}
                  {Array.from({ length: 30 }).map((_, i) => (
                    <div
                      key={i}
                      className="absolute w-2 h-2 rounded-full animate-confetti"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: '-10%',
                        backgroundColor: [
                          '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'
                        ][Math.floor(Math.random() * 6)],
                        animationDelay: `${Math.random() * 0.5}s`,
                        animationDuration: `${2 + Math.random() * 1}s`,
                      }}
                    />
                  ))}

                  {/* Completion Message */}
                  <div className="relative z-10 text-center space-y-3 animate-in slide-in-from-bottom-4 duration-700">
                    <div className="text-6xl mb-2 animate-bounce">🎉</div>
                    <div className="space-y-1">
                      <div className="text-3xl font-bold text-green-400 drop-shadow-lg">
                        Season {celebrationEpisode.season}
                      </div>
                      <div className="text-3xl font-bold text-green-400 drop-shadow-lg">
                        Episode {celebrationEpisode.episode}
                      </div>
                      <div className="text-2xl font-bold text-white drop-shadow-lg mt-2">
                        Complete!
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Title and Info Section */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                {/* Title */}
                <h3 className="font-semibold text-sm text-white line-clamp-2 leading-tight mb-1">
                  {item.name}
                </h3>

                {/* TMDB Rating */}
                {item.rating && (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span>{item.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Quick Complete Button - TV: next-episode shortcut */}
              {showQuickComplete && item.type === 'tv' && getNextEpisodeToWatch() && (() => {
                const nextEp = getNextEpisodeToWatch();
                const { locked, reason } = isQuickCompleteButtonLocked();
                return (
                  <button
                    onClick={locked ? undefined : handleQuickComplete}
                    disabled={locked}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 rounded-full backdrop-blur-md transition-all shadow-xl hover:scale-110 active:scale-95 ${
                      locked
                        ? 'bg-yellow-600/20 border-2 border-yellow-500/80 cursor-not-allowed hover:bg-yellow-600/30'
                        : 'bg-green-600/20 border-2 border-green-500/80 hover:bg-green-600/30'
                    }`}
                    title={locked ? `Cannot mark S${nextEp?.season}E${nextEp?.episode}: ${reason}` : `Mark S${nextEp?.season}E${nextEp?.episode} as watched`}
                  >
                    {locked ? (
                      <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-yellow-400" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-green-400" />
                    )}
                    <span className="text-[10px] md:text-xs font-bold text-white">S{nextEp?.season}E{nextEp?.episode}</span>
                  </button>
                );
              })()}

              {/* Quick Complete Button - Movie: mark watched while in Currently Watching */}
              {showQuickComplete && item.type === 'movie' && !item.watched && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMovieWatchState(item.id, 'watched');
                  }}
                  className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 rounded-full backdrop-blur-md transition-all shadow-xl hover:scale-110 active:scale-95 bg-green-600/20 border-2 border-green-500/80 hover:bg-green-600/30"
                  title="Mark movie as watched"
                >
                  <CheckCircle2 className="h-4 w-4 md:h-5 md:w-5 text-green-400" />
                  <span className="text-[10px] md:text-xs font-bold text-white">Complete</span>
                </button>
              )}
            </div>

          </div>
        ) : (
          <Link href={`/${item.type}/${item.id}`}>
            <div className="space-y-2">
            {/* Poster Image Container with improved gradients */}
            <div
              className="relative aspect-[2/3] rounded-2xl overflow-hidden bg-gradient-to-br from-gray-900 to-black ring-1 ring-white/10 transition-all duration-300 group-hover/card:ring-red-500/50 group-hover/card:shadow-[0_0_30px_-5px_rgba(220,38,38,0.45)] group-hover/card:-translate-y-1"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setHoverRating(null);
              }}
            >
              {/* Poster Image */}
              {item.posterPath ? (
                <Image
                  src={tmdb.getImageUrl(item.posterPath, 'w500')}
                  alt={item.name}
                  fill
                  sizes="(max-width: 768px) 50vw, (max-width: 1200px) 25vw, 20vw"
                  className="object-cover transition-transform duration-500 group-hover/card:scale-105"
                  priority={priority}
                  loading={priority ? undefined : "lazy"}
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWEREiMxUf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  {item.type === 'tv' ? (
                    <Tv className="h-16 w-16 text-gray-600" />
                  ) : (
                    <Film className="h-16 w-16 text-gray-600" />
                  )}
                </div>
              )}
            </div>

            {/* Title and Info Section */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm text-white line-clamp-2 leading-tight mb-1">
                  {item.name}
                </h3>
                {item.rating && (
                  <div className="flex items-center gap-1">
                    <Sparkles className="h-3 w-3 text-yellow-400" />
                    <span className="text-xs font-bold text-yellow-400">{item.rating.toFixed(1)}</span>
                  </div>
                )}
              </div>
              <AddToPlaylistButton
                media={{ id: item.id, type: item.type, title: item.name, posterPath: item.posterPath }}
                className="flex-shrink-0"
              />
            </div>
            </div>
          </Link>
        )}
      </div>

      {/* Season Selector Menu - Mobile Style */}
      {showSeasonMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 z-50 animate-in fade-in duration-200"
            onClick={() => setShowSeasonMenu(false)}
          />

          {/* Menu */}
          <div
            className="fixed inset-x-0 bottom-0 z-50 animate-in slide-in-from-bottom duration-300"
            style={{
              transform: `translateY(${dragOffset}px)`,
              transition: isDragging ? 'none' : 'transform 0.3s ease-out'
            }}
          >
            <div className="bg-gradient-to-b from-gray-900 to-black rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden">
              {/* Handle bar - Draggable area */}
              <div
                className="flex justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing"
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                <div className="w-12 h-1.5 bg-gray-700 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-6 py-4">
                <h3 className="text-xl font-bold text-white truncate">{item.name}</h3>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-sm text-gray-400">
                    {item.watchedSeasons.length}/{item.totalSeasons} seasons watched
                  </span>
                  <div className="flex-1 max-w-[120px] h-1.5 bg-gray-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-600 transition-all duration-500"
                      style={{ width: `${watchedPercentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Season List */}
              <div
                ref={seasonScrollRef}
                className="overflow-y-auto max-h-[calc(85vh-140px)] px-4 pb-6"
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
              {loadingSeasons ? (
                <div className="text-center py-12">
                  <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-red-600 border-t-transparent"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {seasons.filter(seasonHasDisplayableData).map((season) => {
                    const isWatched = item.watchedSeasons.includes(season.season_number);
                    const watchedEpisodeCount = item.watchedEpisodes?.[season.season_number]?.length || 0;
                    const totalEpisodes = season.episodes?.length || season.episode_count || 0;
                    const isExpanded = expandedSeasons.has(season.season_number);

                    // Check if season has any unaired episodes
                    const hasFutureEpisodes = seasonHasUnairedEpisodes(season.episodes);

                    const isLocked = !isWatched && hasFutureEpisodes;

                    return (
                      <div key={season.season_number} className="space-y-2">
                        {/* Season Header - Mobile Optimized */}
                        <div className={`rounded-2xl overflow-hidden ${
                          isWatched
                            ? 'bg-gradient-to-br from-green-950/40 to-green-900/20 ring-2 ring-green-600/30'
                            : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 ring-1 ring-gray-700/50'
                        }`}>
                          <button
                            disabled={isLocked}
                            onClick={() => !isLocked && handleSeasonToggle(season.season_number)}
                            className="w-full p-4 active:scale-[0.98] transition-transform"
                          >
                            <div className="flex items-center gap-3">
                              {/* Season Poster - Smaller on mobile */}
                              <div className="relative w-14 h-20 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0 shadow-lg">
                                {season.poster_path ? (
                                  <Image
                                    src={tmdb.getImageUrl(season.poster_path, 'w200')}
                                    alt={season.name}
                                    width={56}
                                    height={80}
                                    className="object-cover w-full h-full"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Tv className="h-5 w-5 text-gray-600" />
                                  </div>
                                )}
                              </div>

                              {/* Season Details */}
                              <div className="flex-1 min-w-0 text-left">
                                <h4 className="text-white font-bold text-base mb-1.5 line-clamp-1">
                                  {season.name}
                                </h4>

                                {/* Progress Bar */}
                                <div className="mb-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-gray-300">
                                      {watchedEpisodeCount}/{totalEpisodes} episodes
                                    </span>
                                    {season.air_date && (
                                      <span className="text-xs text-gray-500">
                                        {formatTMDBDate(season.air_date, 'yyyy')}
                                      </span>
                                    )}
                                  </div>
                                  <div className="h-1.5 bg-gray-800/70 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full transition-all duration-500 ${
                                        isWatched ? 'bg-green-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${totalEpisodes > 0 ? (watchedEpisodeCount / totalEpisodes) * 100 : 0}%` }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Status Icon - Larger touch target */}
                              <div className="flex-shrink-0">
                                {isWatched ? (
                                  <div className="w-10 h-10 rounded-full bg-green-600/20 border-2 border-green-500 flex items-center justify-center">
                                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                                  </div>
                                ) : isLocked ? (
                                  <div className="w-10 h-10 rounded-full bg-gray-700/50 border-2 border-gray-600 flex items-center justify-center">
                                    <Lock className="h-4 w-4 text-gray-500" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 rounded-full border-2 border-gray-600 flex items-center justify-center">
                                    <Circle className="h-4 w-4 text-gray-500" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </button>

                          {/* Expand/Collapse button - Full width touch target */}
                          {season.episodes && season.episodes.length > 0 && (
                            <button
                              onClick={() => {
                                const newExpanded = new Set(expandedSeasons);
                                if (isExpanded) {
                                  newExpanded.delete(season.season_number);
                                } else {
                                  newExpanded.add(season.season_number);
                                }
                                setExpandedSeasons(newExpanded);
                              }}
                              className="w-full py-2 px-4 flex items-center justify-center gap-2 border-t border-gray-700/50 active:bg-gray-800/50 transition-colors"
                            >
                              <span className="text-xs font-medium text-gray-400">
                                {isExpanded ? 'Hide' : 'Show'} Episodes
                              </span>
                              <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </button>
                          )}
                        </div>

                        {/* Episode List (Expandable) - Mobile Optimized */}
                        {isExpanded && season.episodes && (
                          <div className="pl-2 space-y-2">
                            {season.episodes.map((episode: any) => {
                              const isEpisodeWatched = item.watchedEpisodes?.[season.season_number]?.includes(episode.episode_number) || false;

                              // Check if episode is in the future
                              // Unaired = future date, or no date with nothing aired after it
                              const isDisabled = !isEpisodeAvailable(episode, season.episodes || []);

                              return (
                                <button
                                  key={episode.episode_number}
                                  onClick={async () => {
                                    if (isDisabled && !isEpisodeWatched) return;

                                    if (isEpisodeWatched) {
                                      await markEpisodeUnwatched(item.id, season.season_number, episode.episode_number);
                                    } else {
                                      await markEpisodeWatched(item.id, season.season_number, episode.episode_number);

                                      // Check if season is now complete
                                      const newWatchedCount = (item.watchedEpisodes?.[season.season_number]?.length || 0) + 1;
                                      if (newWatchedCount >= season.episodes.length) {
                                        // Auto-complete season
                                        await markSeasonWatched(item.id, season.season_number);
                                      }
                                    }
                                  }}
                                  disabled={isDisabled && !isEpisodeWatched}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all active:scale-[0.98] ${
                                    isEpisodeWatched
                                      ? 'bg-gradient-to-r from-green-950/40 to-green-900/20 ring-1 ring-green-600/30'
                                      : isDisabled
                                      ? 'bg-gray-800/20 ring-1 ring-gray-700/20 opacity-50 cursor-not-allowed'
                                      : 'bg-gray-800/30 hover:bg-gray-800/50 ring-1 ring-gray-700/30'
                                  }`}
                                >
                                  {/* Episode Number Badge */}
                                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
                                    isEpisodeWatched
                                      ? 'bg-green-600/30 text-green-400 ring-1 ring-green-500/50'
                                      : 'bg-gray-700/50 text-gray-400 ring-1 ring-gray-600/50'
                                  }`}>
                                    {episode.episode_number}
                                  </div>

                                  {/* Episode Details */}
                                  <div className="flex-1 min-w-0 text-left">
                                    <div className={`text-sm font-medium line-clamp-1 ${
                                      isEpisodeWatched ? 'text-white' : 'text-gray-200'
                                    }`}>
                                      {episode.name}
                                    </div>
                                    {episode.air_date && (
                                      <div className="text-xs text-gray-500 mt-0.5">
                                        {formatTMDBDate(episode.air_date, 'MMM d, yyyy')}
                                      </div>
                                    )}
                                  </div>

                                  {/* Checkmark / Lock Icon */}
                                  <div className="flex-shrink-0">
                                    {isEpisodeWatched ? (
                                      <div className="w-6 h-6 rounded-full bg-green-600 flex items-center justify-center">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    ) : isDisabled ? (
                                      <div className="w-6 h-6 rounded-full bg-gray-700/50 flex items-center justify-center">
                                        <Lock className="h-3 w-3 text-gray-500" />
                                      </div>
                                    ) : (
                                      <div className="w-6 h-6 rounded-full border-2 border-gray-600" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* (WatchlistMobileMenu long-press menu removed — tapping a card now opens
          the redesigned ItemDetailDialog which provides all the same actions.) */}

      {/* Desktop Season Selection Dialog */}
      {showDesktopSeasonDialog && item.type === 'tv' && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 z-50 animate-in fade-in duration-200"
            onClick={() => setShowDesktopSeasonDialog(false)}
          />

          {/* Dialog - Centered on desktop */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDesktopSeasonDialog(false)}
          >
            <div
              className="bg-gradient-to-b from-gray-900 to-black rounded-3xl shadow-2xl max-h-[85vh] overflow-hidden max-w-2xl w-full animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-800">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white truncate">{item.name}</h3>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-sm text-gray-400">
                      {item.watchedSeasons.length}/{item.totalSeasons} seasons watched
                    </span>
                    <div className="flex-1 max-w-[120px] h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-red-600 transition-all duration-500"
                        style={{ width: `${watchedPercentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDesktopSeasonDialog(false)}
                  className="flex-shrink-0 ml-4 w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Season List */}
              <div className="overflow-y-auto max-h-[calc(85vh-140px)] px-6 py-6">
                {loadingSeasons ? (
                  <div className="text-center py-12">
                    <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-red-600 border-t-transparent"></div>
                    <p className="text-gray-400 mt-4">Loading season details...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {seasons.filter(seasonHasDisplayableData).map((season) => {
                      const isWatched = item.watchedSeasons.includes(season.season_number);
                      const watchedEpisodeCount = item.watchedEpisodes?.[season.season_number]?.length || 0;
                      const totalEpisodes = season.episodes?.length || season.episode_count || 0;
                      const isExpanded = expandedSeasons.has(season.season_number);

                      // Check if season has any unaired episodes
                      const hasFutureEpisodes = seasonHasUnairedEpisodes(season.episodes);

                      const isLocked = !isWatched && hasFutureEpisodes;

                      return (
                        <div key={season.season_number} className="space-y-3">
                          {/* Season Header - Desktop Optimized */}
                          <div className={`rounded-2xl overflow-hidden transition-all ${
                            isWatched
                              ? 'bg-gradient-to-br from-green-950/40 to-green-900/20 ring-2 ring-green-600/30'
                              : 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 ring-1 ring-gray-700/50 hover:ring-red-600/50'
                          }`}>
                            <button
                              disabled={isLocked}
                              onClick={() => !isLocked && handleSeasonToggle(season.season_number)}
                              className="w-full p-5 transition-all hover:bg-white/5"
                            >
                              <div className="flex items-center gap-4">
                                {/* Season Poster */}
                                <div className="w-16 h-24 rounded-lg overflow-hidden bg-gray-900 flex-shrink-0 shadow-lg">
                                  {season.poster_path ? (
                                    <Image
                                      src={tmdb.getImageUrl(season.poster_path, 'w200')}
                                      alt={season.name}
                                      width={64}
                                      height={96}
                                      className="object-cover w-full h-full"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Tv className="h-6 w-6 text-gray-600" />
                                    </div>
                                  )}
                                </div>

                                {/* Season Details */}
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-white font-bold text-lg mb-2 truncate">
                                    {season.name}
                                  </h4>

                                  {/* Progress Bar */}
                                  <div className="mb-2">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <span className="text-sm font-medium text-gray-300">
                                        {watchedEpisodeCount}/{totalEpisodes} episodes
                                      </span>
                                      {season.air_date && (
                                        <span className="text-sm text-gray-500">
                                          {formatTMDBDate(season.air_date, 'yyyy')}
                                        </span>
                                      )}
                                    </div>
                                    <div className="h-2 bg-gray-800/70 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full transition-all duration-500 ${
                                          isWatched ? 'bg-green-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${totalEpisodes > 0 ? (watchedEpisodeCount / totalEpisodes) * 100 : 0}%` }}
                                      />
                                    </div>
                                  </div>

                                </div>

                                {/* Status Icon */}
                                <div className="flex-shrink-0">
                                  {isWatched ? (
                                    <div className="w-12 h-12 rounded-full bg-green-600/20 border-2 border-green-500 flex items-center justify-center">
                                      <CheckCircle2 className="h-6 w-6 text-green-400" />
                                    </div>
                                  ) : isLocked ? (
                                    <div className="w-12 h-12 rounded-full bg-gray-700/50 border-2 border-gray-600 flex items-center justify-center">
                                      <Lock className="h-5 w-5 text-gray-500" />
                                    </div>
                                  ) : (
                                    <div className="w-12 h-12 rounded-full border-2 border-gray-600 flex items-center justify-center">
                                      <Circle className="h-5 w-5 text-gray-500" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>

                            {/* Expand/Collapse button - Full width */}
                            {season.episodes && season.episodes.length > 0 && (
                              <button
                                onClick={() => {
                                  const newExpanded = new Set(expandedSeasons);
                                  if (isExpanded) {
                                    newExpanded.delete(season.season_number);
                                  } else {
                                    newExpanded.add(season.season_number);
                                  }
                                  setExpandedSeasons(newExpanded);
                                }}
                                className="w-full py-3 px-5 flex items-center justify-center gap-2 border-t border-gray-700/50 hover:bg-gray-800/50 transition-colors"
                              >
                                <span className="text-sm font-medium text-gray-400">
                                  {isExpanded ? 'Hide' : 'Show'} Episodes
                                </span>
                                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </button>
                            )}
                          </div>

                          {/* Episode List (Expandable) - Desktop Optimized */}
                          {isExpanded && season.episodes && (
                            <div className="pl-4 space-y-2">
                              {season.episodes.map((episode: any) => {
                                const isEpisodeWatched = item.watchedEpisodes?.[season.season_number]?.includes(episode.episode_number) || false;

                                // Unaired = future date, or no date with nothing aired after it
                                const isDisabled = !isEpisodeAvailable(episode, season.episodes || []);

                                return (
                                  <button
                                    key={episode.episode_number}
                                    onClick={async () => {
                                      if (isDisabled && !isEpisodeWatched) return;

                                      if (isEpisodeWatched) {
                                        await markEpisodeUnwatched(item.id, season.season_number, episode.episode_number);
                                      } else {
                                        await markEpisodeWatched(item.id, season.season_number, episode.episode_number);

                                        // Check if season is now complete
                                        const newWatchedCount = (item.watchedEpisodes?.[season.season_number]?.length || 0) + 1;
                                        if (newWatchedCount >= season.episodes.length) {
                                          // Auto-complete season
                                          await markSeasonWatched(item.id, season.season_number);
                                        }
                                      }
                                    }}
                                    disabled={isDisabled && !isEpisodeWatched}
                                    className={`w-full flex items-center gap-4 p-3.5 rounded-xl transition-all ${
                                      isEpisodeWatched
                                        ? 'bg-gradient-to-r from-green-950/40 to-green-900/20 ring-1 ring-green-600/30 hover:ring-green-500/50'
                                        : isDisabled
                                        ? 'bg-gray-800/20 ring-1 ring-gray-700/20 opacity-50 cursor-not-allowed'
                                        : 'bg-gray-800/30 hover:bg-gray-800/50 ring-1 ring-gray-700/30 hover:ring-gray-600/50'
                                    }`}
                                  >
                                    {/* Episode Number Badge */}
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                                      isEpisodeWatched
                                        ? 'bg-green-600/30 text-green-400 ring-1 ring-green-500/50'
                                        : 'bg-gray-700/50 text-gray-400 ring-1 ring-gray-600/50'
                                    }`}>
                                      {episode.episode_number}
                                    </div>

                                    {/* Episode Details */}
                                    <div className="flex-1 min-w-0 text-left">
                                      <div className={`text-sm font-medium truncate ${
                                        isEpisodeWatched ? 'text-white' : 'text-gray-200'
                                      }`}>
                                        {episode.name}
                                      </div>
                                      {episode.air_date && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          {formatTMDBDate(episode.air_date, 'MMM d, yyyy')}
                                        </div>
                                      )}
                                    </div>

                                    {/* Checkmark / Lock Icon */}
                                    <div className="flex-shrink-0">
                                      {isEpisodeWatched ? (
                                        <div className="w-7 h-7 rounded-full bg-green-600 flex items-center justify-center">
                                          <Check className="h-4 w-4 text-white" />
                                        </div>
                                      ) : isDisabled ? (
                                        <div className="w-7 h-7 rounded-full bg-gray-700/50 flex items-center justify-center">
                                          <Lock className="h-3.5 w-3.5 text-gray-500" />
                                        </div>
                                      ) : (
                                        <div className="w-7 h-7 rounded-full border-2 border-gray-600" />
                                      )}
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Rating Picker Modal */}
      {showRatingPicker && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/70 z-50 animate-in fade-in duration-200"
            onClick={() => setShowRatingPicker(false)}
          />

          {/* Dialog */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowRatingPicker(false)}
          >
            <div
              className="bg-gradient-to-b from-gray-900 to-black rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm animate-in zoom-in-95 duration-200"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-gray-800">
                <div>
                  <h3 className="text-lg font-bold text-white">Rate {item.name}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Tap stars to rate</p>
                </div>
                <button
                  onClick={() => setShowRatingPicker(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>

              {/* Star Rating */}
              <div className="p-6">
                <div className="flex justify-center gap-3 mb-6">
                  {[...Array(5)].map((_, i) => {
                    const isFilled = (i + 1) <= ((hoverRating || item.personalRank || 0) / 2);
                    return (
                      <button
                        key={i}
                        onClick={() => handleStarClick(i)}
                        onMouseEnter={() => handleStarHover(i)}
                        onMouseLeave={() => setHoverRating(null)}
                        className="transition-transform hover:scale-125 active:scale-110"
                      >
                        <Star
                          className={`h-12 w-12 ${
                            isFilled
                              ? 'fill-yellow-500 text-yellow-500'
                              : 'fill-gray-700 text-gray-700'
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>

                {/* Display current rating */}
                <div className="text-center mb-4">
                  <div className="text-3xl font-bold text-yellow-500">
                    {(hoverRating || item.personalRank || 0) ? `${(hoverRating || item.personalRank || 0) / 2} / 5` : 'Tap to rate'}
                  </div>
                </div>

                {/* Remove Rating Button */}
                {item.personalRank && (
                  <button
                    onClick={async () => {
                      // Remove from both rating systems
                      if (user) {
                        await ratingsService.removeUserRating(user.uid, item.id, item.type);
                        await setPersonalRank(item.id, 0); // 0 is converted to undefined in store
                      }
                      setShowRatingPicker(false);
                    }}
                    className="w-full mt-4 py-3 rounded-xl bg-red-900/30 text-red-400 text-sm font-medium hover:bg-red-900/50 transition-colors ring-1 ring-red-800/50"
                  >
                    Remove Rating
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Memoize to prevent unnecessary re-renders
export const WatchlistCard = memo(WatchlistCardComponent, (prevProps, nextProps) => {
  // Deep comparison of watchlist item
  const prevItem = prevProps.item;
  const nextItem = nextProps.item;

  // Check all relevant properties
  return (
    prevItem.id === nextItem.id &&
    prevItem.hasNewContent === nextItem.hasNewContent &&
    prevItem.watched === nextItem.watched &&
    prevItem.currentlyWatching === nextItem.currentlyWatching &&
    prevItem.personalRank === nextItem.personalRank &&
    JSON.stringify(prevItem.watchedSeasons) === JSON.stringify(nextItem.watchedSeasons) &&
    JSON.stringify(prevItem.watchedEpisodes) === JSON.stringify(nextItem.watchedEpisodes) &&
    prevProps.showQuickComplete === nextProps.showQuickComplete
  );
});
